import React, { useState, useEffect } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import Title from './Title';
import TablePagination from '@mui/material/TablePagination';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import {TextField, InputAdornment, IconButton, Box, Button} from '@mui/material';
import {Search as SearchIcon, Clear as ClearIcon} from '@mui/icons-material';

const ConversationList = ({user, accessToken}) => {
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredConversations(conversations);
    } else {
      const filtered = conversations.filter(conversation =>
        (conversation.assistant ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (conversation.description ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        new Date(conversation.date).toLocaleDateString().includes(searchTerm)
      );
      setFilteredConversations(filtered);
    }
    setPage(0);
  }, [searchTerm, conversations]);

  useEffect(() => {    
    const fetchConversations = async () => {
      const bodyData = JSON.stringify({ access_token: accessToken })
      try {
        const response = await fetch(process.env.REACT_APP_API_BASE_URL + 'actions/get_conversations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: bodyData
        });
        if (!response.ok) {
          throw new Error('Could not fetch conversations data');
        }
        const data = await response.json();
        const chats = data.conversations.map(conversation => ({
          key: conversation.key,
          description: conversation.description,
          assistant: conversation.assistant,
          date: conversation.date
        }));      
  
        setConversations(chats);
        setFilteredConversations(chats);
      } catch (error) {
        console.error('Error fetching conversations:', error); 
      }
    };
  
    fetchConversations();
  }, [user.id, user.mail]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event, conversation) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setSelectedConversation(conversation);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedConversation(null);
  };

  const handleDelete = async (event) => {
    event.stopPropagation();
    event.preventDefault();

    const bodyData = JSON.stringify({
      access_token: accessToken,
      conversation_key: selectedConversation.key
    });
    setOpenDialog(false);

    try {
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + 'actions/delete_conversation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: bodyData
      });

      const result = await response.json();

      if (result.status === "Success") {
        const updatedConversations = conversations.filter(conversation => conversation.key !== selectedConversation.key);
        setConversations(updatedConversations);
      } else {
        console.error('Failed to delete conversation');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }

    handleMenuClose();
  };

  const handleOpenDialog = async (event) => {
    event.stopPropagation();
    event.preventDefault();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    handleMenuClose();
  };

  return (
    <div>
      <Title>History of Conversations</Title>
      
      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search History of Conversations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setSearchTerm('')}
                  edge="end"
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {searchTerm && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Found {filteredConversations.length} conversation{filteredConversations.length !== 1 ? 's' : ''} matching "{searchTerm}"
          </Typography>
        )}
      </Box>

      {filteredConversations.length === 0 && !searchTerm ? (
        <Typography variant="h6" color="text.secondary" align="center">
          No conversations yet. Start chatting with an AI Assistant to see your conversation history here.
        </Typography>
      ) : filteredConversations.length === 0 && searchTerm ? (
        <Typography variant="h6" color="text.secondary" align="center">
          No conversations found matching "{searchTerm}"
          <br />
          <Button 
            variant="text" 
            onClick={() => setSearchTerm('')}
            sx={{ mt: 1 }}
          >
            Clear search
          </Button>
        </Typography>
      ) : (
        <>
          <List>
            {filteredConversations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(conversation => (
              <ListItem 
                key={conversation.key} 
                alignItems="flex-start" 
                button 
                component={Link} 
                to={`/conversation/${conversation.key}`}
                onClick={(event) => {
                  if (anchorEl) {
                    event.preventDefault();
                  }
                }} 
              >
                <ListItemAvatar>
                  <Avatar aria-label="conversation">
                    {conversation.assistant[0]}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`Conversation with ${conversation.assistant}`}
                  secondary={
                    <>
                      <Typography
                        component="span"
                        variant="body2"
                        color="textPrimary"
                      >
                        {new Date(conversation.date).toLocaleDateString()}
                      </Typography>
                      {` - ${conversation.description}`}
                    </>
                  }
                />
                <IconButton edge="end" aria-label="more" onClick={(event) => handleMenuOpen(event, conversation)}>
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={handleOpenDialog}>Delete</MenuItem>
                </Menu>
              </ListItem>
            ))}
          </List>

          <TablePagination
            component="div"
            count={filteredConversations.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the conversation with "{selectedConversation?.assistant}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ConversationList;
